import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { IUserWithTokens } from '../../../helpers/types';
import {
    asyncDeleteTokenAction,
    asyncGetUsersAction,
    asyncUpdateUserAction,
    asyncCreateUserAction,
    asyncSetUserRoleAction,
} from './usersAction';
import { toast } from 'react-toastify';
import { tostSetting } from '../../../helpers/scripts';

interface UsersState {
    users: IUserWithTokens[];
    totalUsers: number;
    currentPage: number;
    limit: number;
    isLoading: boolean;
    error: string;
}

const initialState: UsersState = {
    users: [],
    totalUsers: 0,
    currentPage: 1,
    limit: 10,
    isLoading: false,
    error: '',
};

const usersSlice = createSlice({
    name: 'usersSlice',
    initialState,
    reducers: {
        setCurrentPageUsers(state, action) {
            state.currentPage = action.payload;
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(
                asyncGetUsersAction.fulfilled,
                (state: UsersState, action: PayloadAction<any>) => {
                    const { rows, count } = action.payload;
                    state.isLoading = false;
                    state.error = '';
                    state.users = rows || [];
                    state.totalUsers = count || 0;
                },
            )
            .addCase(asyncGetUsersAction.pending, (state: UsersState) => {
                state.isLoading = true;
            })
            .addCase(
                asyncGetUsersAction.rejected,
                (state: UsersState, action: PayloadAction<any>) => {
                    state.isLoading = false;
                    state.error = action.payload;
                },
            )
            .addCase(
                asyncUpdateUserAction.fulfilled,
                (state: UsersState, action: PayloadAction<IUserWithTokens>) => {
                    state.isLoading = false;
                    state.error = '';
                    const updated = action.payload;
                    state.users = state.users.map((user) =>
                        user.id === updated.id ? { ...updated } : user,
                    );
                    toast.success('User successfully updated', tostSetting);
                },
            )
            .addCase(asyncUpdateUserAction.pending, (state: UsersState) => {
                state.isLoading = true;
            })
            .addCase(
                asyncUpdateUserAction.rejected,
                (state: UsersState, action: PayloadAction<any>) => {
                    state.isLoading = false;
                    state.error = action.payload;
                    toast.error("Can't update user", tostSetting);
                },
            )
            .addCase(
                asyncDeleteTokenAction.fulfilled,
                (
                    state: UsersState,
                    action: PayloadAction<{ userId: number; tokenId: number }>,
                ) => {
                    state.isLoading = false;
                    state.error = '';
                    const { userId, tokenId } = action.payload;
                    state.users = state.users.map((user) => {
                        if (user.id !== userId) return user;
                        return {
                            ...user,
                            tokens: (user.tokens || []).filter(
                                (token) => token.id !== tokenId,
                            ),
                        };
                    });
                    toast.success('Token successfully deleted', tostSetting);
                },
            )
            .addCase(asyncDeleteTokenAction.pending, (state: UsersState) => {
                state.isLoading = true;
            })
            .addCase(
                asyncDeleteTokenAction.rejected,
                (state: UsersState, action: PayloadAction<any>) => {
                    state.isLoading = false;
                    state.error = action.payload;
                    toast.error("Can't delete token", tostSetting);
                },
            )
            .addCase(
                asyncCreateUserAction.fulfilled,
                (state: UsersState, action: PayloadAction<IUserWithTokens>) => {
                    state.isLoading = false;
                    state.error = '';
                    state.users.push(action.payload);
                    state.totalUsers = state.totalUsers + 1;
                    toast.success('User successfully created', tostSetting);
                },
            )
            .addCase(asyncCreateUserAction.pending, (state: UsersState) => {
                state.isLoading = true;
            })
            .addCase(
                asyncCreateUserAction.rejected,
                (state: UsersState, action: PayloadAction<any>) => {
                    state.isLoading = false;
                    state.error = action.payload;
                    toast.error("Can't create user", tostSetting);
                },
            )
            .addCase(
                asyncSetUserRoleAction.fulfilled,
                (state: UsersState, action: PayloadAction<IUserWithTokens>) => {
                    state.isLoading = false;
                    state.error = '';
                    const updated = action.payload;
                    state.users = state.users.map((user) =>
                        user.id === updated.id ? { ...updated } : user,
                    );
                    toast.success('User role successfully updated', tostSetting);
                },
            )
            .addCase(asyncSetUserRoleAction.pending, (state: UsersState) => {
                state.isLoading = true;
            })
            .addCase(
                asyncSetUserRoleAction.rejected,
                (state: UsersState, action: PayloadAction<any>) => {
                    state.isLoading = false;
                    state.error = action.payload;
                    toast.error("Can't update user role", tostSetting);
                },
            );
    },
});

export const { setCurrentPageUsers } = usersSlice.actions;
export default usersSlice.reducer;
